import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Gift, Loader2, Trophy, Upload, Download, AlertTriangle, RefreshCw, Check, CalendarClock,
  MessageCircle, ArrowLeft, ImageIcon, Video, ChevronRight,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type IgAccount = { id: string; username: string | null; displayName: string | null; profilePhotoUrl: string | null };
type Post = { mediaPk: string; url: string; thumbnailUrl: string | null; caption: string; postedAt: string | null };
type Winner = { userId: string; username: string; fullName: string | null; profilePicUrl: string | null };
type DrawResult = { winners: Winner[]; totalParticipants: number; totalComments: number; complete: boolean; seed: number };
type SorteoHistory = {
  id: string;
  accountUsername: string | null;
  winners: { username: string; fullName: string | null }[];
  totalParticipants: number;
  imageUrl: string | null;
  videoUrl: string | null;
  createdAt: string;
};

const BRAND_COLORS = ['#016fff', '#7c3aed', '#e11d48', '#059669', '#ea580c', '#0f172a'];

export function Sorteos() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);

  const [account, setAccount] = useState<IgAccount | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [winnersCount, setWinnersCount] = useState(1);
  const [title, setTitle] = useState('');
  const [brandColor, setBrandColor] = useState('#016fff');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const [drawing, setDrawing] = useState(false);
  const [result, setResult] = useState<DrawResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [imageMediaId, setImageMediaId] = useState<string | null>(null);
  const [videoMediaId, setVideoMediaId] = useState<string | null>(null);

  const accountsQuery = useQuery({ queryKey: ['sorteos', 'accounts'], queryFn: () => api<{ accounts: IgAccount[] }>('/api/sorteos/accounts') });
  const accounts = accountsQuery.data?.accounts ?? [];

  const historyQuery = useQuery({ queryKey: ['sorteos', 'history'], queryFn: () => api<{ sorteos: SorteoHistory[] }>('/api/sorteos/history') });
  const history = historyQuery.data?.sorteos ?? [];

  const postsQuery = useQuery({
    queryKey: ['sorteos', 'posts', account?.id],
    queryFn: () => api<{ posts: Post[] }>(`/api/sorteos/posts?accountId=${account!.id}`),
    enabled: !!account && step === 2,
  });
  const posts = postsQuery.data?.posts ?? [];

  const [visibleCount, setVisibleCount] = useState(10);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const requested = useRef<Record<string, boolean>>({});
  const visiblePosts = posts.slice(0, visibleCount);
  const visibleKey = visiblePosts.map((p) => p.mediaPk).join(',');

  useEffect(() => {
    const missing = visiblePosts.map((p) => p.mediaPk).filter((pk) => !requested.current[pk]);
    if (missing.length === 0) return;
    missing.forEach((pk) => { requested.current[pk] = true; });
    api<{ counts: Record<string, number | null> }>('/api/sorteos/comment-counts', { method: 'POST', body: JSON.stringify({ pks: missing.slice(0, 12) }) })
      .then((r) => setCounts((c) => ({ ...c, ...r.counts })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);

  async function uploadLogo(file: File) {
    setLogoUploading(true); setError(null);
    try {
      const presign = await api<{ id: string; uploadUrl: string; publicUrl: string; headers: Record<string, string> }>(
        '/api/media/upload-url', { method: 'POST', body: JSON.stringify({ mimeType: file.type, sizeBytes: file.size, originalName: file.name }) });
      await fetch(presign.uploadUrl, { method: 'PUT', headers: presign.headers, body: file });
      await api('/api/media/confirm', { method: 'POST', body: JSON.stringify({ id: presign.id }) });
      setLogoUrl(presign.publicUrl);
    } catch { setError('No se pudo subir el logo.'); } finally { setLogoUploading(false); }
  }

  /** Draw winners, jump to the result step and auto-generate both deliverables. */
  async function runDraw(reroll = false) {
    if (!post) return;
    setDrawing(true); setError(null);
    setResult(null); setImageUrl(null); setVideoUrl(null); setImageMediaId(null); setVideoMediaId(null);
    try {
      const body: Record<string, unknown> = { post: post.url, winners: winnersCount };
      if (reroll) body.seed = Math.floor(Math.random() * 0xffffffff);
      const r = await api<DrawResult>('/api/sorteos/draw', { method: 'POST', body: JSON.stringify(body) });
      setResult(r);
      setStep(4);
      void generate(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo hacer el sorteo.');
    } finally { setDrawing(false); }
  }

  /** Render image + video in parallel, show each as it lands, then persist. */
  async function generate(r: DrawResult) {
    const payload = (format: 'image' | 'video') => ({
      winners: r.winners.map((w) => ({ username: w.username, profilePicUrl: w.profilePicUrl })),
      logoUrl, title, brandColor, seed: r.seed, format,
    });
    const imgP = api<{ url: string; mediaId: string }>('/api/sorteos/render', { method: 'POST', body: JSON.stringify(payload('image')) })
      .then((o) => { setImageUrl(o.url); setImageMediaId(o.mediaId); return o; }).catch(() => null);
    const vidP = api<{ url: string; mediaId: string }>('/api/sorteos/render', { method: 'POST', body: JSON.stringify(payload('video')) })
      .then((o) => { setVideoUrl(o.url); setVideoMediaId(o.mediaId); return o; }).catch(() => null);
    const [img, vid] = await Promise.all([imgP, vidP]);
    try {
      await api('/api/sorteos/save', {
        method: 'POST',
        body: JSON.stringify({
          accountUsername: account?.username ?? null,
          winners: r.winners.map((w) => ({ username: w.username, fullName: w.fullName })),
          totalParticipants: r.totalParticipants,
          totalComments: r.totalComments,
          imageUrl: img?.url ?? null,
          videoUrl: vid?.url ?? null,
          imageMediaId: img?.mediaId ?? null,
          videoMediaId: vid?.mediaId ?? null,
        }),
      });
      void qc.invalidateQueries({ queryKey: ['sorteos', 'history'] });
    } catch { /* saving history is best-effort */ }
  }

  function publish(mediaId: string) {
    navigate('/dashboard/posts/new', { state: { prefillMediaId: mediaId, prefillAccountId: account?.id, prefillStory: true } });
  }

  function restart() {
    setStep(1); setAccount(null); setPost(null); setResult(null);
    setImageUrl(null); setVideoUrl(null); setImageMediaId(null); setVideoMediaId(null);
    setError(null); setVisibleCount(10); setCounts({}); requested.current = {};
  }

  return (
    <div className="max-w-4xl">
      {/* header */}
      <div className="flex items-center gap-3 mb-1">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Gift size={18} /></span>
        <h1 className="text-2xl font-bold text-slate-900">Sorteos</h1>
      </div>
      <p className="text-slate-500 mb-6">Elige un post de Instagram, saca 1–3 ganadores al azar y genera la imagen y el vídeo listos para publicar.</p>

      {/* stepper */}
      <Stepper step={step} />

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* ── STEP 1 — account + history ── */}
      {step === 1 && (
        <>
          <Panel title="Elige la cuenta de Instagram">
            {accountsQuery.isLoading ? <Spinner /> : accounts.length === 0 ? <Empty text="No tienes ninguna cuenta de Instagram conectada." /> : (
              <div className="flex flex-wrap gap-2">
                {accounts.map((a) => (
                  <button key={a.id} onClick={() => { setAccount(a); setPost(null); setVisibleCount(10); setCounts({}); requested.current = {}; setStep(2); }}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm hover:border-brand-300 hover:bg-brand-50 transition">
                    {a.profilePhotoUrl ? <img src={a.profilePhotoUrl} alt="" className="h-6 w-6 rounded-full object-cover" referrerPolicy="no-referrer" /> : <span className="h-6 w-6 rounded-full bg-slate-200" />}
                    <span className="font-medium">@{a.username ?? a.displayName ?? '—'}</span>
                  </button>
                ))}
              </div>
            )}
          </Panel>

          {history.length > 0 && (
            <div className="mt-8">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Últimos sorteos realizados</h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {history.map((s) => <HistoryCard key={s.id} s={s} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── STEP 2 — post ── */}
      {step === 2 && account && (
        <Panel title="Elige el post del sorteo" onBack={() => setStep(1)}>
          {postsQuery.isLoading ? <Spinner /> : posts.length === 0 ? <Empty text="No se encontraron posts recientes en esta cuenta." /> : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {visiblePosts.map((p) => {
                  const count = counts[p.mediaPk];
                  return (
                    <button key={p.mediaPk} onClick={() => { setPost(p); setStep(3); }}
                      className={cn('relative aspect-square rounded-lg overflow-hidden border-2 bg-slate-100 transition',
                        post?.mediaPk === p.mediaPk ? 'border-brand-500 ring-2 ring-brand-200' : 'border-transparent hover:border-slate-300')} title={p.caption}>
                      {p.thumbnailUrl && <img src={p.thumbnailUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />}
                      <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-brand-700/95 via-brand-600/70 to-transparent px-2 pt-4 pb-1.5 text-white text-xs font-semibold">
                        <MessageCircle size={12} className="shrink-0" />
                        {count === undefined ? <Loader2 size={11} className="animate-spin opacity-80" /> : count === null ? '—' : count.toLocaleString('es-ES')}
                      </span>
                    </button>
                  );
                })}
              </div>
              {posts.length > visibleCount && (
                <div className="mt-3 flex justify-center">
                  <button onClick={() => setVisibleCount((c) => c + 10)} className="text-sm px-4 py-2 rounded-lg border border-slate-200 text-slate-600 font-medium hover:border-brand-300 hover:text-brand-600 transition">
                    Cargar más ({posts.length - visibleCount})
                  </button>
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {/* ── STEP 3 — configure + draw ── */}
      {step === 3 && post && (
        <Panel title="Configura y sortea" onBack={() => setStep(2)}>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Número de ganadores</label>
              <div className="inline-flex rounded-lg bg-slate-100 p-1">
                {[1, 2, 3].map((n) => (
                  <button key={n} onClick={() => setWinnersCount(n)} className={cn('px-4 py-1.5 text-sm font-medium rounded-md transition', winnersCount === n ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}>{n}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Título (opcional)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="¡Tenemos ganador!" maxLength={60} className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Color de marca</label>
              <div className="flex gap-1.5">
                {BRAND_COLORS.map((c) => (
                  <button key={c} onClick={() => setBrandColor(c)} className={cn('h-8 w-8 rounded-full border-2 transition', brandColor === c ? 'border-slate-900 scale-110' : 'border-white shadow')} style={{ backgroundColor: c }} aria-label={c} />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Logo (opcional)</label>
              <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm cursor-pointer hover:border-brand-300 hover:text-brand-600 transition">
                {logoUploading ? <Loader2 size={15} className="animate-spin" /> : logoUrl ? <Check size={15} className="text-emerald-600" /> : <Upload size={15} />}
                {logoUrl ? 'Logo subido — cambiar' : 'Subir logo'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
              </label>
            </div>
          </div>
          <button onClick={() => runDraw(false)} disabled={drawing} className="btn-primary mt-6 gap-2">
            {drawing ? <Loader2 size={16} className="animate-spin" /> : <Trophy size={16} />}
            {drawing ? 'Sorteando…' : `Sortear ${winnersCount === 1 ? 'ganador' : `${winnersCount} ganadores`}`}
          </button>
        </Panel>
      )}

      {/* ── STEP 4 — result ── */}
      {step === 4 && result && (
        <Panel title="Resultado" onBack={() => setStep(3)}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" />
                {result.winners.length === 1 ? '¡Tenemos ganador!' : `¡${result.winners.length} ganadores!`}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{result.totalParticipants} participantes · {result.totalComments} comentarios</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => runDraw(true)} disabled={drawing} className="btn-secondary gap-2 text-sm"><RefreshCw size={14} /> Repetir sorteo</button>
              <button onClick={restart} className="btn-secondary text-sm">Nuevo sorteo</button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            {result.winners.map((w) => (
              <div key={w.userId} className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3">
                {w.profilePicUrl ? <img src={w.profilePicUrl} alt="" className="h-12 w-12 rounded-full object-cover" referrerPolicy="no-referrer" /> : <span className="h-12 w-12 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold">{w.username[0]?.toUpperCase()}</span>}
                <div><p className="font-semibold text-slate-900">@{w.username}</p>{w.fullName && <p className="text-xs text-slate-500">{w.fullName}</p>}</div>
              </div>
            ))}
          </div>

          {/* auto-generated deliverables with looping preview */}
          <div className="grid sm:grid-cols-2 gap-5">
            <Deliverable label="Imagen story 9:16" icon={ImageIcon}>
              {imageUrl
                ? <><img src={imageUrl} alt="Sorteo" className="w-full rounded-lg border border-slate-200" /><ActionButtons url={imageUrl} name="sorteo.png" mediaId={imageMediaId} onPublish={publish} /></>
                : <MediaSkeleton label="Generando imagen…" />}
            </Deliverable>
            <Deliverable label="Vídeo MP4" icon={Video}>
              {videoUrl
                ? <><video src={videoUrl} autoPlay loop muted playsInline className="w-full rounded-lg border border-slate-200 bg-black" /><ActionButtons url={videoUrl} name="sorteo.mp4" mediaId={videoMediaId} onPublish={publish} /></>
                : <MediaSkeleton label="Generando vídeo… (unos segundos)" />}
            </Deliverable>
          </div>
        </Panel>
      )}
    </div>
  );
}

/* ── building blocks ─────────────────────────────────────── */

function Stepper({ step }: { step: number }) {
  const labels = ['Cuenta', 'Post', 'Ajustes', 'Resultado'];
  return (
    <div className="flex items-center gap-2 mb-5">
      {labels.map((l, i) => {
        const n = i + 1;
        const state = n < step ? 'done' : n === step ? 'active' : 'todo';
        return (
          <div key={l} className="flex items-center gap-2">
            <span className={cn('inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
              state === 'done' ? 'bg-emerald-500 text-white' : state === 'active' ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-500')}>
              {state === 'done' ? <Check size={13} /> : n}
            </span>
            <span className={cn('text-xs font-medium', state === 'active' ? 'text-slate-900' : 'text-slate-400')}>{l}</span>
            {n < labels.length && <span className="w-6 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}

function Panel({ title, onBack, children }: { title: string; onBack?: () => void; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {onBack && (
          <button onClick={onBack} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition" aria-label="Volver">
            <ArrowLeft size={16} />
          </button>
        )}
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Spinner() { return <div className="flex items-center gap-2 text-slate-400 py-4 text-sm"><Loader2 size={15} className="animate-spin" /> Cargando…</div>; }
function Empty({ text }: { text: string }) { return <p className="text-sm text-slate-400 py-3">{text}</p>; }

function Deliverable({ label, icon: Icon, children }: { label: string; icon: typeof ImageIcon; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Icon size={13} /> {label}</p>
      <div className="max-w-[240px] space-y-2">{children}</div>
    </div>
  );
}

function MediaSkeleton({ label }: { label: string }) {
  return (
    <div className="aspect-[9/16] max-w-[240px] rounded-lg border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-2 text-slate-400">
      <Loader2 size={20} className="animate-spin" />
      <span className="text-xs">{label}</span>
    </div>
  );
}

function ActionButtons({ url, name, mediaId, onPublish }: { url: string; name: string; mediaId: string | null; onPublish: (id: string) => void }) {
  return (
    <>
      {mediaId && (
        <button onClick={() => onPublish(mediaId)} className="btn-primary w-full justify-center gap-2 text-sm"><CalendarClock size={15} /> Programar / publicar</button>
      )}
      <a href={url} download={name} target="_blank" rel="noreferrer" className="btn-secondary w-full justify-center gap-2 text-sm"><Download size={15} /> Descargar</a>
    </>
  );
}

/* ── Detail page for a saved giveaway (/dashboard/sorteos/:id) ── */

type SorteoDetailData = {
  id: string;
  accountUsername: string | null;
  winners: { username: string; fullName: string | null }[];
  totalParticipants: number;
  totalComments: number;
  imageUrl: string | null;
  videoUrl: string | null;
  imageMediaId: string | null;
  videoMediaId: string | null;
  createdAt: string;
};

export function SorteoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detailQuery = useQuery({ queryKey: ['sorteos', 'detail', id], queryFn: () => api<{ sorteo: SorteoDetailData }>(`/api/sorteos/${id}`), retry: false });
  const accountsQuery = useQuery({ queryKey: ['sorteos', 'accounts'], queryFn: () => api<{ accounts: IgAccount[] }>('/api/sorteos/accounts') });
  const s = detailQuery.data?.sorteo;
  const accountId = accountsQuery.data?.accounts.find((a) => a.username && s?.accountUsername && a.username === s.accountUsername)?.id ?? null;

  function publish(mediaId: string) {
    navigate('/dashboard/posts/new', { state: { prefillMediaId: mediaId, prefillAccountId: accountId, prefillStory: true } });
  }
  const date = s ? new Date(s.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="max-w-3xl">
      <Link to="/dashboard/sorteos" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"><ArrowLeft size={15} /> Volver a sorteos</Link>
      {detailQuery.isLoading ? <Spinner /> : !s ? <Empty text="Sorteo no encontrado." /> : (
        <>
          <div className="flex items-center gap-3 mb-1">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-500"><Trophy size={18} /></span>
            <h1 className="text-2xl font-bold text-slate-900">{s.winners.length === 1 ? 'Ganador del sorteo' : 'Ganadores del sorteo'}</h1>
          </div>
          <p className="text-slate-500 mb-6">{s.accountUsername ? `@${s.accountUsername} · ` : ''}{date} · {s.totalParticipants} participantes</p>

          <div className="flex flex-wrap gap-3 mb-6">
            {s.winners.map((w, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                <span className="h-11 w-11 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-bold">{w.username[0]?.toUpperCase()}</span>
                <div><p className="font-semibold text-slate-900">@{w.username}</p>{w.fullName && <p className="text-xs text-slate-500">{w.fullName}</p>}</div>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {s.imageUrl && (
              <Deliverable label="Imagen story 9:16" icon={ImageIcon}>
                <img src={s.imageUrl} alt="Sorteo" className="w-full rounded-lg border border-slate-200" />
                <ActionButtons url={s.imageUrl} name="sorteo.png" mediaId={s.imageMediaId} onPublish={publish} />
              </Deliverable>
            )}
            {s.videoUrl && (
              <Deliverable label="Vídeo MP4" icon={Video}>
                <video src={s.videoUrl} autoPlay loop muted playsInline className="w-full rounded-lg border border-slate-200 bg-black" />
                <ActionButtons url={s.videoUrl} name="sorteo.mp4" mediaId={s.videoMediaId} onPublish={publish} />
              </Deliverable>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function HistoryCard({ s }: { s: SorteoHistory }) {
  const date = new Date(s.createdAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <Link to={`/dashboard/sorteos/${s.id}`} className="group flex gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-brand-300 hover:shadow-md transition">
      {s.imageUrl
        ? <img src={s.imageUrl} alt="" className="h-24 w-[54px] shrink-0 rounded-md object-cover border border-slate-200" />
        : <span className="h-24 w-[54px] shrink-0 rounded-md bg-slate-100 flex items-center justify-center text-slate-300"><Gift size={18} /></span>}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-slate-400">{s.accountUsername ? `@${s.accountUsername} · ` : ''}{date}</p>
        <div className="mt-0.5 space-y-0.5">
          {s.winners.map((w, i) => (
            <p key={i} className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1"><Trophy size={12} className="text-amber-500 shrink-0" />@{w.username}</p>
          ))}
        </div>
        <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 group-hover:gap-1.5 transition-all">Ver sorteo <ChevronRight size={13} /></span>
      </div>
    </Link>
  );
}
